// Stacked diff list for the current-order vs. uploaded-document compare
// step. Every field row follows the same interaction regardless of conflict
// status: click a value to pick it, or click Edit to switch to manual entry
// (no separate confirm step) — typing writes straight into the resolution,
// so the input is always the single source of truth and can't drift from
// what's displayed. Conflict rows just have two clickable values (current,
// new) instead of one.

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useTranslation } from "react-i18next"
import {
  CompareField,
  CompareFieldKey,
  ResolutionState,
  fieldStatus,
} from "./compareFields"

const CompareFieldsList = ({
  fields,
  resolution,
  onPick,
  onManualEdit,
}: {
  fields: CompareField[]
  resolution: ResolutionState
  onPick: (key: CompareFieldKey, source: "old" | "new") => void
  onManualEdit: (key: CompareFieldKey, value: string) => void
}) => {
  const { t } = useTranslation()
  const startCustom = (key: CompareFieldKey) =>
    onManualEdit(key, resolution[key].value)

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col divide-y rounded-md border">
        {fields.map((field) => {
          const status = fieldStatus(field)
          const isCustom = resolution[field.key].source === "manual"

          if (status === "conflict") {
            const source = resolution[field.key].source
            return (
              <div
                key={field.key}
                className="flex flex-col gap-2 px-3 py-2 text-sm"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">
                    {t(field.label)}
                  </span>
                  {!isCustom && (
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant={source === "old" ? "default" : "outline"}
                        className={
                          source === "old" ? undefined : "line-through"
                        }
                        onClick={() => onPick(field.key, "old")}
                      >
                        {field.oldValue || "—"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={source === "new" ? "default" : "outline"}
                        className={
                          source === "new" ? undefined : "line-through"
                        }
                        onClick={() => onPick(field.key, "new")}
                      >
                        {field.newValue || "—"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => startCustom(field.key)}
                      >
                        {t("common.edit")}
                      </Button>
                    </div>
                  )}
                </div>
                {isCustom && (
                  <Input
                    autoFocus
                    value={resolution[field.key].value}
                    onChange={(e) => onManualEdit(field.key, e.target.value)}
                  />
                )}
              </div>
            )
          }

          return (
            <div
              key={field.key}
              className="flex flex-col gap-2 px-3 py-2 text-sm"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">{t(field.label)}</span>
                {!isCustom && (
                  <div className="flex items-center gap-2">
                    <span
                      className={
                        status === "auto-fill"
                          ? "font-medium text-amber-700 dark:text-amber-500"
                          : undefined
                      }
                    >
                      {resolution[field.key].value || "—"}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => startCustom(field.key)}
                    >
                      {t("common.edit")}
                    </Button>
                  </div>
                )}
              </div>
              {isCustom && (
                <Input
                  autoFocus
                  value={resolution[field.key].value}
                  onChange={(e) => onManualEdit(field.key, e.target.value)}
                />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default CompareFieldsList
